import { Injectable } from "@angular/core";
import { GridLayoutService } from "@irb-common/interfaces/grid-layout.service";
import { DeleteCellComponent } from "@irb-common/components/grid/renderers/delete.cell.component";
import { DeleteHeaderComponent } from "@irb-common/components/grid/renderers/delete.header.component";
import { TruncateTooltipCellComponent } from '@irb-common/components/grid/renderers/truncatetooltip.cell.component';
import { ComAccountsComponent } from "@irb/irb-editors/editor-portfolio/com-accounts/com-accounts.component";
import { UtilityService } from "@irb-common/services/utility.service";
import { DecimalPipe } from '@angular/common';

@Injectable({
    providedIn: "root"
})
export class ComAccountsGridLayoutService implements GridLayoutService<ComAccountsComponent> {
    sizeFor: any;

    constructor(private util: UtilityService, private decimalPipe: DecimalPipe) {
        this.sizeFor = {
            "accountNumber": 90,
            "accountName": 120,
            "accountsubType": 60,
            "marketValue": 80,
            "accountLocationType": 95,
            "masterNumber": 60,
            "smaSymbolName": 100,
            "fundingAccountNumber": 90,
            "isDoNotTrade": 70,
            "isDividendReinvest": 60,
            "isCapGainsReinvest": 60,
            "isGlobal": 80,
            "cashMinimum": 80,
            "cashMaximum": 80,
            "cashTarget": 80,
            "curAmount": 80,
            "nextScheduleDate": 110
        };
    }

    getFrameworkComponents() {
        return {
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DeleteCellComponent": DeleteCellComponent,
            "TruncateTooltipCellComponent": TruncateTooltipCellComponent
        };
    }

    roundDollarString(s: any) {
            if (s && (s.includes("$") || s.includes("$-"))) {
                const isNegativeValue = s.includes("$-") ? true : false;
                const onlyValue = isNegativeValue ? s.replace("$-", "") : s.replace("$", "");
                const roundedValue = this.decimalPipe.transform(Math.round(parseFloat(onlyValue)));
                return isNegativeValue ? "($" + roundedValue + ")" : "$" + roundedValue;
            } else if (s && (s.includes("%") || s.includes("-"))) {
                const isNegativeValue = s.includes("-") ? true : false;
                const onlyValue = isNegativeValue ? s.replace(/[-%]/gi, "") : s.replace("%", "");
                return isNegativeValue ? "(" + onlyValue + "%)" : + onlyValue + "%";
            } else {
                return s;
            }
        }

    getCashMinimum = (params: any) => {
        if (!params.data?.isSMA) {
            if ((params.data?.isGlobal && params.data?.useModelCash && params.data?.globalCashMinimum != null) || params.data?.useModelCashAtAccount) {
                return 'model';
            } else if (params.data?.isGlobal) {
                return this.roundDollarString(params.data?.globalCashMinimum);
            } else {
                return this.roundDollarString(params.data?.cashMinimum);
            }
        } else {
            return 'n/a';
        }
    }

    getCashTarget = (params: any) => {
        if (!params.data?.isSMA) {
            if ((params.data?.isGlobal && params.data?.useModelCash && params.data?.globalCashTarget != null) || params.data?.useModelCashAtAccount) {
                return 'model';
            } else if (params.data?.isGlobal) {
                return this.roundDollarString(params.data?.globalCashTarget);
            } else {
                return this.roundDollarString(params.data?.cashTarget);
            }
        } else {
            return 'n/a';
        }
    }

    getCashMaximum = (params: any) => {
        if (!params.data?.isSMA) {
            if ((params.data?.isGlobal && params.data?.useModelCash && params.data?.globalCashMaximum != null) || params.data?.useModelCashAtAccount) {
                return 'model';
            } else if (params.data?.isGlobal) {
                return this.roundDollarString(params.data.globalCashMaximum);
            } else {
                return this.roundDollarString(params.data?.cashMaximum);
            }
        } else {
            return 'n/a';
        }
    }

    getColumnDefs(parent: ComAccountsComponent) {
        return [{
            headerName: "Delete All",
            headerComponent: "DeleteHeaderComponent",
            headerComponentParams: {
                message: "Remove all accounts from the portfolio?",
                note: "Settings (such as maintained cash) will be deleted.",
                proceed: parent.deleteAll.bind(parent),
                cancel: parent.cancelDelete.bind(parent)
            },
            cellRenderer : "DeleteCellComponent",
            cellRendererParams: {
                message: "Remove this account from the portfolio?",
                note: "Settings (such as maintained cash) will be deleted.",
                proceed: parent.delete.bind(parent),
                cancel: parent.cancelDelete.bind(parent)
            },
            resizable: false,
            minWidth: 25,
            maxWidth: 25,
            width: 25,
            pinned: "left"
        },
        {
            headerName: "Accounts",
            children: [
                {
                    headerName: "Acct#",
                    field: "accountNumber",
                    minWidth: this.sizeFor.accountNumber,
                    width: this.sizeFor.accountNumber,
                    sort: "asc",
                    comparator: this.util.sortString.bind(parent),
                    pinned: "left",
                    cellRenderer : "cellRendererFormatter",
                    cellRendererParams: {
                        link: (params: any) => {
                            return (params.value != null && params.value.length > 0);
                        },
                        onClick: (function(data: any) {
                          parent.showEditAccount(data);
                        }).bind(parent),
                        calltoactionTemplateFunc: parent.ENABLE_FLYOUT ? (params: any) => parent.getTemplate(params) : undefined
                    }
                },
                {
                    headerName: "AcctName",
                    field: "accountName",
                    minWidth: this.sizeFor.accountName,
                    width: this.sizeFor.accountName,
                    comparator: this.util.sortString.bind(parent),
                    pinned: "left",
                    cellRenderer: "TruncateTooltipCellComponent"
                },
                {
                    headerName: "Type",
                    field: "accountsubType",
                    minWidth: this.sizeFor.accountsubType,
                    width: this.sizeFor.accountsubType,
                    cellRenderer: "TruncateTooltipCellComponent",
                    pinned: "left"
                },
                {
                    headerName: "MktValue",
                    field: "marketValue",
                    cellRenderer : 'cellRendererFormatter',
                    cellRendererParams: {type: 'currency'},
                    floatingFilterComponent: 'irbInputFloatingFilterComponent',
                    filter: 'irbAgNumberColumnFilterComponent',
                    minWidth: this.sizeFor.marketValue,
                    width: this.sizeFor.marketValue,
                    pinned: "left"
                },
                {
                    headerName: "Taxability",
                    field: "accountLocationType",
                    minWidth: this.sizeFor.accountLocationType,
                    width: this.sizeFor.accountLocationType,
                    cellRenderer: "TruncateTooltipCellComponent",
                    pinned: "left"
                },
                {
                    headerName: "Master Account", //RepCode change to master as per schwab standards
                    field: "masterAcctNumber",
                    minWidth: this.sizeFor.masterNumber,
                    width: this.sizeFor.masterNumber,
                    cellRenderer: "TruncateTooltipCellComponent",
                    pinned: "left"
                }
            ]
        },
        {
            headerName: "Maintained Cash",
            children: [
                {
                    headerName: "Global",
                    field: "isGlobal",
                    suppressFiltersToolPanel: true,
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true},
                    minWidth: this.sizeFor.isGlobal,
                    width: this.sizeFor.isGlobal
                },
                {
                    headerName: "Cash Min",
                    field: "cashMinimum",
                    minWidth: this.sizeFor.cashMinimum,
                    width: this.sizeFor.cashMinimum,
                    valueFormatter: (params: any) => {
                        return this.getCashMinimum(params);
                    }
                },
                {
                    headerName: "Cash Target",
                    field: "cashTarget",
                    minWidth: this.sizeFor.cashTarget,
                    width: this.sizeFor.cashTarget,
                    valueFormatter: (params: any) => {
                        return this.getCashTarget(params);
                    }
                },
                {
                    headerName: "Cash Max",
                    field: "cashMaximum",
                    minWidth: this.sizeFor.cashMaximum,
                    width: this.sizeFor.cashMaximum,
                    valueFormatter: (params: any) => {
                        return this.getCashMaximum(params);
                    }
                }
            ]
        },
        {
            headerName: "Set-Aside Cash",

            children: [
                {
                    headerName: "CurrAmt",
                    field: "curAmount",
                    minWidth: this.sizeFor.curAmount,
                    width: this.sizeFor.curAmount
                },
                {
                    headerName: "Next Schd",
                    field: "nextScheduleDate",
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: {type: 'date', format: 'MM/dd/yyyy'},
                    minWidth: this.sizeFor.nextScheduleDate,
                    width: this.sizeFor.nextScheduleDate
                },
                {
                    headerName: 'Dist Hold',
                    field: 'autoCashHoldForDistAmt',
                    minWidth: 80,
                    width: 80,
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: { type: 'currency' },
                    hide: !parent.isDistHoldCashEnabled
                }
            ]
        },     
        {
            headerName: "Special Treatment",
            children: [
                {
                    headerName: "Frozen",
                    field: "isDoNotTrade",
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true},
                    minWidth: this.sizeFor.isDoNotTrade,
                    width: this.sizeFor.isDoNotTrade,
                },
                {
                    headerName: "DIVs",
                    field: "isDividendReinvest",
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true},
                    minWidth: this.sizeFor.isDividendReinvest,
                    width: this.sizeFor.isDividendReinvest,
                },
                {
                    headerName: "CGs",
                    field: "isCapGainsReinvest",
                    cellRenderer: 'cellRendererFormatter',
                    cellRendererParams: { type: 'checkbox', isCheckboxDisabled: true},
                    minWidth: this.sizeFor.isCapGainsReinvest,
                    width: this.sizeFor.isCapGainsReinvest,
                }
            ]
        },
        {
            headerName: "Treat as SMA",
            children: [
                {
                    headerName: "SMA Symbol",
                    field: "smaSymbolName",
                    minWidth: this.sizeFor.smaSymbolName,
                    width: this.sizeFor.smaSymbolName
                },
                {
                    headerName: "Fndng Acct#",
                    field: "fundingAccountNumber",
                    minWidth: this.sizeFor.fundingAccountNumber,
                    width: this.sizeFor.fundingAccountNumber
                }
            ]
        }
    ];
    }
}
